import React, { useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ConsultationMessage, ConsultationThread, GuidedClientAccess, User } from '../types';
import { getTranslation } from '../utils/translations';
import { getUserDisplayName } from '../utils/userDisplay';
import {
    ensureConsultationThread,
    markConsultationThreadReadForClient,
    sendConsultationMessage,
    subscribeConsultationMessages,
} from '../utils/consultationThreads';
import { getInitials } from '../hooks/useConsultantBannerData';
import AvatarImage from './AvatarImage';
import XIcon from './icons/XIcon';

interface ConsultationModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    allUsers: User[];
}

const ConsultationModal: React.FC<ConsultationModalProps> = ({ isOpen, onClose, user, allUsers }) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language || 'en', key);
    const [accessRows, setAccessRows] = useState<GuidedClientAccess[]>([]);
    const [activeConsultantId, setActiveConsultantId] = useState<string | null>(null);
    const [thread, setThread] = useState<ConsultationThread | null>(null);
    const [messages, setMessages] = useState<ConsultationMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [consultantProfile, setConsultantProfile] = useState<User | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen || !user.guidedClientId) return;
        const q = query(
            collection(db, 'guidedClientAccess'),
            where('clientId', '==', user.guidedClientId),
            where('isActive', '==', true),
        );
        return onSnapshot(q, (snap) => {
            const rows = snap.docs.map((d) => ({ ...(d.data() as Omit<GuidedClientAccess, 'id'>), id: d.id }));
            setAccessRows(rows);
            if (!activeConsultantId && rows.length > 0) {
                setActiveConsultantId(rows[0].consultantId);
            }
        });
    }, [isOpen, user.guidedClientId, activeConsultantId]);

    const activeAccess = accessRows.find((r) => r.consultantId === activeConsultantId);

    useEffect(() => {
        if (!activeConsultantId) {
            setConsultantProfile(null);
            return;
        }
        return onSnapshot(doc(db, 'users', activeConsultantId), (snap) => {
            if (!snap.exists()) {
                setConsultantProfile(null);
                return;
            }
            setConsultantProfile({ ...(snap.data() as Omit<User, 'uid'>), uid: activeConsultantId });
        });
    }, [activeConsultantId]);

    useEffect(() => {
        if (!isOpen || !user.guidedClientId || !activeAccess) {
            setThread(null);
            return;
        }
        let cancelled = false;
        ensureConsultationThread({
            clientId: user.guidedClientId,
            consultantId: activeAccess.consultantId,
            clientUid: user.uid,
            consultantUid: activeAccess.consultantId,
            householdId: activeAccess.householdId,
        }).then((created) => {
            if (!cancelled) setThread(created);
        }).catch((err) => setError(err instanceof Error ? err.message : t('errorSaving')));
        return () => { cancelled = true; };
    }, [isOpen, user.guidedClientId, user.uid, activeAccess?.consultantId, activeAccess?.householdId]);

    useEffect(() => {
        if (!thread?.id) {
            setMessages([]);
            return;
        }
        return subscribeConsultationMessages(thread.id, setMessages);
    }, [thread?.id]);

    useEffect(() => {
        if (!isOpen || !thread?.id) return;
        markConsultationThreadReadForClient(thread.id).catch(() => undefined);
    }, [isOpen, thread?.id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const consultantUser = consultantProfile ?? allUsers.find((u) => u.uid === activeConsultantId);
    const consultantLabel = consultantUser
        ? getUserDisplayName(consultantUser)
        : t('userTypeNutritionConsultant');
    const consultantAvatarUrl = consultantUser?.avatarUrl;
    const consultantInitials = getInitials(consultantLabel);

    if (!isOpen) return null;

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!thread?.id || !draft.trim()) return;
        setLoading(true);
        setError('');
        try {
            await sendConsultationMessage(thread.id, 'client', draft, user.uid);
            setDraft('');
        } catch (err) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('consultationBox')}</h2>
                    <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {accessRows.length > 1 && (
                    <div className="p-3 border-b border-slate-100 dark:border-slate-800 flex gap-2 overflow-x-auto">
                        {accessRows.map((row) => {
                            const c = allUsers.find((u) => u.uid === row.consultantId);
                            const label = c?.nickname || c?.firstName || c?.email || row.consultantId;
                            return (
                                <button
                                    key={row.id}
                                    type="button"
                                    onClick={() => setActiveConsultantId(row.consultantId)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-black whitespace-nowrap ${
                                        activeConsultantId === row.consultantId
                                            ? 'bg-brand-sage text-white'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                    }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="px-5 py-3 border-b border-slate-50 dark:border-slate-800 flex items-center gap-3">
                    <AvatarImage
                        src={consultantAvatarUrl}
                        alt={consultantLabel}
                        className="h-10 w-10 rounded-full object-cover border-2 border-white dark:border-slate-800 shadow-sm bg-white dark:bg-slate-800"
                        fallback={
                            <div className="h-10 w-10 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center text-xs font-black border-2 border-white dark:border-slate-800">
                                {consultantInitials}
                            </div>
                        }
                    />
                    <div className="min-w-0">
                        <p className="text-sm font-black text-slate-800 dark:text-slate-100 truncate">{consultantLabel}</p>
                        <p className="text-[10px] font-bold text-slate-400">
                            {consultantUser?.consultantCertification?.trim() || t('userTypeNutritionConsultant')}
                        </p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[240px]">
                    {messages.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-8">{t('consultationEmpty')}</p>
                    ) : (
                        messages.map((msg) => {
                            const isMine = msg.senderUid === user.uid;
                            return (
                                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                    {!isMine && (
                                        <AvatarImage
                                            src={consultantAvatarUrl}
                                            alt=""
                                            className="h-7 w-7 rounded-full object-cover me-2 mt-1 shrink-0 self-end"
                                            fallback={
                                                <div className="h-7 w-7 rounded-full bg-brand-sage/15 text-brand-sage flex items-center justify-center text-[9px] font-black me-2 mt-1 shrink-0 self-end">
                                                    {consultantInitials}
                                                </div>
                                            }
                                        />
                                    )}
                                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                                        isMine
                                            ? 'bg-brand-sage text-white rounded-br-md'
                                            : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-md'
                                    }`}>
                                        {msg.text}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={bottomRef} />
                </div>

                <form onSubmit={handleSend} className="p-4 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                    <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={t('consultationPlaceholder')}
                        className="flex-1 p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800"
                    />
                    <button
                        type="submit"
                        disabled={loading || !draft.trim()}
                        className="px-4 py-2 bg-brand-sage text-white font-black rounded-xl disabled:opacity-50"
                    >
                        {t('send')}
                    </button>
                </form>
                {error && <p className="px-4 pb-3 text-sm text-red-600">{error}</p>}
            </div>
        </div>
    );
};

export default ConsultationModal;
